import { Tables } from '../../db_types'
import { z } from 'zod'
import { getMonthDateRange, isValidDateOnlyString } from '@dolfin/utils/dates'
import Payouts from '../payouts'
import DolfinCurrency from '../../currency'
import {
  DolfinRpc,
  MonthlyStatementReviewFlowSummary,
  NotificationStatementReference,
  StartStatementsReviewResult,
  StatementMonthRow,
  StatementPeriodReference,
  StatementResetEligibility
} from '../../rpc'
import {
  getStatementPaidRollbackStatus,
  notifyStatementPaid,
  StatementPaidNotificationError
} from './payment-notifications'
import {
  getStatementPaymentIneligibilityReason,
  isStatementEligibleForPayment,
  StatementPaymentIneligibilityReason
} from './payment-eligibility'

export type GetUnreviewedDiscrepanciesCountParams = {
  rpcClient: DolfinRpc
  statementId: number
}

export type GetNotificationReferencesParams = {
  rpcClient: DolfinRpc
  statementIds: number[]
}

export type GetStatementPeriodReferencesParams = {
  rpcClient: DolfinRpc
  statementIds: number[]
}

export type EnsureStatementsForPeriodRangeParams = {
  rpcClient: DolfinRpc
  organizationId: number
  startDate: string
  endDate: string
}

export type StartStatementsReviewParams = {
  rpcClient: DolfinRpc
  organizationId: number
  statementIds: number[]
}

export type GetMonthlyReviewFlowParams = {
  rpcClient: DolfinRpc
  organizationId: number
  startDate: string
  endDate: string
}

export type StatementReadContext = {
  rpcClient: DolfinRpc
  organizationId: number
  actorId: string
}

export type StatementListScope = {
  organizationId: number
  managedTeamIds: number[] | null
}

const readContextSchema = z.object({
  organizationId: z.number().int().positive(),
  actorId: z.string().trim().min(1)
})

const monthListSchema = z
  .object({
    month: z.string(),
    cursor: z
      .string()
      .regex(/^[1-9]\d*$/)
      .transform(Number)
      .refine(Number.isSafeInteger)
      .optional(),
    pageSize: z.number().int().min(1).max(100).default(25)
  })
  .strict()

export type ListStatementsMonthParams = StatementReadContext & z.input<typeof monthListSchema>

export type StatementReadSummary = StatementMonthRow & {
  total: number
  currency: string
  paymentReadiness: {
    eligible: boolean
    reason: StatementPaymentIneligibilityReason | 'already_paid' | null
    hasStatementApprovalFlow: boolean
    hasPayoutValidationFlow: boolean
  }
}

const changeStatusSchema = z
  .object({
    statementIds: z.array(z.number().int().positive()).min(1).max(100),
    operation: z.enum(['start_review', 'mark_paid', 'mark_unpaid', 'reset_to_pending_approval'])
  })
  .strict()

export type ChangeStatementStatusParams = StatementReadContext & z.input<typeof changeStatusSchema>

export type StatementStatusChangeResult = {
  statementId: number
  outcome: 'updated' | 'skipped' | 'rejected'
  reason:
    | 'permission_denied'
    | 'unavailable'
    | 'not_open'
    | 'already_paid'
    | 'not_paid'
    | 'not_in_review'
    | 'discrepancies'
    | StatementPaymentIneligibilityReason
    | 'stale'
    | 'execution_failed'
    | 'notification_failed'
    | 'rollback_failed'
    | 'ineligible'
    | null
}

const statementActionSchema = z.object({ statementId: z.number().int().positive() }).strict()

export type ApproveStatementParams = StatementReadContext & z.input<typeof statementActionSchema>

export type AddStatementIssueParams = StatementReadContext & z.input<typeof statementActionSchema>

const manualPayoutDateSchema = z.string().refine(isValidDateOnlyString, 'Expected YYYY-MM-DD')
const manualPayoutSchema = z
  .object({
    statementId: z.number().int().positive(),
    kind: z.enum(['compensation', 'deduction']),
    amount: z.number().finite().positive(),
    description: z.string().trim().min(1),
    compensationType: z.enum(['bonus', 'commission', 'clawback']),
    effectiveDate: manualPayoutDateSchema,
    periodStart: manualPayoutDateSchema,
    periodEnd: manualPayoutDateSchema,
    categoryId: z.number().int().positive().nullish(),
    exchangeRateMethod: z.enum(['organization_settings', 'custom']).nullish(),
    exchangeRateDate: manualPayoutDateSchema.nullish(),
    customRate: z.number().finite().positive().nullish()
  })
  .strict()
  .refine(input => input.periodStart <= input.periodEnd, 'Compensation period is reversed')
  .refine(
    input => (input.kind === 'deduction') === (input.compensationType === 'clawback'),
    'Deductions require clawback; compensations require bonus or commission'
  )
  .refine(
    input => input.exchangeRateMethod !== 'custom' || input.customRate != null,
    'Custom exchange rate is required'
  )
  .refine(
    input => input.exchangeRateMethod !== 'organization_settings' || input.exchangeRateDate != null,
    'Exchange rate date is required'
  )

export type AddStatementManualPayoutParams = StatementReadContext &
  z.input<typeof manualPayoutSchema>

export {
  monthListSchema as statementMonthListInputSchema,
  changeStatusSchema as statementChangeStatusInputSchema,
  statementActionSchema as statementActionInputSchema,
  manualPayoutSchema as statementManualPayoutInputSchema
}

export type StatementIssueResult = {
  issueId: number
  statementId: number
  status: Tables<'issues'>['status']
  notificationStatus: 'created' | 'failed'
}

export type StatementApprovalResult = {
  statementId: number
  approvalId: null
  outcome: 'updated' | 'skipped'
  reason: 'already_approved' | null
  status: Tables<'statements'>['status'] | null
  remainingApproverIds: string[] | null
  notificationStatus: 'created' | 'failed' | 'not_attempted'
}

export default class Statements {
  private readonly statement: Tables<'statements'>
  private rpcClient: DolfinRpc

  constructor({ rpcClient, statement }: { rpcClient: DolfinRpc; statement: Tables<'statements'> }) {
    this.rpcClient = rpcClient
    this.statement = statement
  }

  static async getListScope(params: StatementReadContext): Promise<StatementListScope> {
    const { organizationId, actorId } = readContextSchema.parse(params)
    const { rpcClient } = params
    if (!(await rpcClient.isActiveOrganizationMember(actorId, organizationId))) {
      throw new Error('Statement access denied')
    }
    if (await rpcClient.isActiveOrganizationAdmin(actorId, organizationId)) {
      return { organizationId, managedTeamIds: null }
    }

    // shortcut: use the existing membership query's row limit; paginate if actors outgrow it.
    const memberships = await rpcClient.listProfileCurrentTeamMembershipsSetup(
      organizationId,
      actorId
    )
    const managedTeamIds = [
      ...new Set(memberships.filter(row => row.role === 'manager').map(row => row.team_id))
    ]
    if (managedTeamIds.length === 0) throw new Error('Statement access denied')
    return { organizationId, managedTeamIds }
  }

  static async getVisibleStatement(params: StatementReadContext & { statementId: number }) {
    const { organizationId } = readContextSchema.parse(params)
    const statementId = z.number().int().positive().parse(params.statementId)
    const statement = await params.rpcClient.getStatementById(statementId, organizationId)
    if (!statement || statement.organization_id !== organizationId) {
      throw new Error('Statement is unavailable')
    }
    return statement
  }

  static async listMonth(params: ListStatementsMonthParams) {
    const { rpcClient, organizationId, actorId, ...input } = params
    const { month, cursor, pageSize } = monthListSchema.parse(input)
    const range = getMonthDateRange(month)
    if (!range) throw new Error('Invalid statement month; expected YYYY-MM')

    const scope = await Statements.getListScope({ rpcClient, organizationId, actorId })
    const rows = await rpcClient.getStatementMonthRows({
      ...scope,
      startDate: range.start,
      endDate: range.end,
      afterId: cursor,
      limit: pageSize + 1
    })
    const items = rows.slice(0, pageSize)
    return {
      items: await Statements.getReadSummaries(rpcClient, organizationId, items),
      nextCursor: rows.length > pageSize ? String(items[items.length - 1].id) : null
    }
  }

  private static async getReadSummaries(
    rpcClient: DolfinRpc,
    organizationId: number,
    rows: StatementMonthRow[]
  ): Promise<StatementReadSummary[]> {
    if (rows.length === 0) return []
    const [organization, config] = await Promise.all([
      rpcClient.getOrgCurrency(organizationId),
      rpcClient.getOrganizationValidationProcessConfig(organizationId)
    ])
    return Promise.all(
      rows.map(async row => {
        const flowConfig = {
          hasStatementApprovalFlow: config.statementApprovalFlow,
          hasPayoutValidationFlow: config.payoutValidationFlow
        }
        const eligibility = { ...row, ...flowConfig }
        return {
          ...row,
          total: await rpcClient.getStatementPayoutTotal({
            organizationId,
            profileId: row.profile_id,
            periodStart: row.period_start
          }),
          currency: organization.currency,
          paymentReadiness: {
            ...flowConfig,
            eligible: isStatementEligibleForPayment(eligibility),
            reason:
              row.status === 'paid'
                ? 'already_paid'
                : (getStatementPaymentIneligibilityReason(eligibility) ?? null)
          }
        }
      })
    )
  }

  static async getDetail(params: StatementReadContext & { statementId: number }) {
    const statement = await Statements.getVisibleStatement(params)
    const { rpcClient } = params
    const row = await rpcClient.getStatementReadRow(statement.id, statement.organization_id)
    if (!row || row.organization_id !== statement.organization_id) {
      throw new Error('Statement is unavailable')
    }
    const [summaries, relations] = await Promise.all([
      Statements.getReadSummaries(rpcClient, statement.organization_id, [row]),
      rpcClient.getStatementReadRelations(statement.id, statement.organization_id)
    ])
    const payouts = [
      ...relations.payouts,
      ...relations.payoutInstallments.map(installment => installment.payouts)
    ]
    const payoutIds = [...new Set(payouts.map(payout => payout.id))]
    const changes = await rpcClient.getStatementReadChanges(
      statement.id,
      statement.organization_id,
      payoutIds
    )
    const lastCalculationTime = payouts.reduce<number | null>((latest, payout) => {
      const time = Date.parse(payout.execution?.created_at ?? '')
      return Number.isFinite(time) && (latest === null || time > latest) ? time : latest
    }, null)
    return {
      ...summaries[0],
      ...relations,
      ...changes,
      lastCalculationAt:
        lastCalculationTime === null ? null : new Date(lastCalculationTime).toISOString()
    }
  }

  static async changeStatus(
    params: ChangeStatementStatusParams
  ): Promise<StatementStatusChangeResult[]> {
    const { rpcClient, organizationId, actorId, ...input } = params
    readContextSchema.parse({ organizationId, actorId })
    const { operation, statementIds } = changeStatusSchema.parse(input)
    const results: StatementStatusChangeResult[] = []

    for (const statementId of new Set(statementIds)) {
      try {
        // Recheck each target: permissions and state may change during a batch.
        if (!(await rpcClient.isActiveOrganizationAdmin(actorId, organizationId))) {
          results.push({ statementId, outcome: 'rejected', reason: 'permission_denied' })
          continue
        }
        const statement = await rpcClient.getStatementReadRow(statementId, organizationId)
        if (!statement || statement.organization_id !== organizationId) {
          results.push({ statementId, outcome: 'rejected', reason: 'unavailable' })
          continue
        }
        if (operation === 'start_review') {
          if (statement.status !== 'open') {
            results.push({ statementId, outcome: 'skipped', reason: 'not_open' })
            continue
          }
          const result = await Statements.startReview({
            rpcClient,
            organizationId,
            statementIds: [statementId]
          })
          const updated = result.updatedStatementIds.includes(statementId)
          results.push({
            statementId,
            outcome: updated ? 'updated' : 'skipped',
            reason: updated ? null : 'stale'
          })
          continue
        }
        if (operation === 'reset_to_pending_approval') {
          const [eligibility] = await Statements.getResetEligibility(rpcClient, [statementId])
          if (!eligibility?.canReset) {
            results.push({
              statementId,
              outcome: 'skipped',
              reason: eligibility?.reason ?? 'stale'
            })
            continue
          }
          await Statements.resetToPendingApproval(rpcClient, statementId)
          results.push({ statementId, outcome: 'updated', reason: null })
          continue
        }
        if (operation === 'mark_paid' && statement.status === 'paid') {
          results.push({ statementId, outcome: 'skipped', reason: 'already_paid' })
          continue
        }
        if (operation === 'mark_unpaid' && statement.status !== 'paid') {
          results.push({ statementId, outcome: 'skipped', reason: 'not_paid' })
          continue
        }
        const config = await rpcClient.getOrganizationValidationProcessConfig(organizationId)
        const rollbackStatus = getStatementPaidRollbackStatus(
          config.statementApprovalFlow,
          config.payoutValidationFlow
        )
        if (operation === 'mark_paid') {
          const eligibility = {
            ...statement,
            hasStatementApprovalFlow: config.statementApprovalFlow,
            hasPayoutValidationFlow: config.payoutValidationFlow
          }
          if (!isStatementEligibleForPayment(eligibility)) {
            results.push({
              statementId,
              outcome: 'skipped',
              reason: getStatementPaymentIneligibilityReason(eligibility) ?? 'stale'
            })
            continue
          }
        }
        const updated = await rpcClient.changeStatementPaymentStatus(
          operation === 'mark_paid' && statement.status !== 'paid'
            ? { organizationId, statementId, expectedStatus: statement.status, status: 'paid' }
            : { organizationId, statementId, expectedStatus: 'paid', status: rollbackStatus }
        )
        if (!updated) {
          results.push({ statementId, outcome: 'skipped', reason: 'stale' })
          continue
        }
        if (operation === 'mark_paid') {
          await notifyStatementPaid({
            rpcClient,
            currentUserId: actorId,
            organizationId,
            statementIds: [statementId],
            rollback: async () => {
              const rolledBack = await rpcClient.changeStatementPaymentStatus({
                organizationId,
                statementId,
                expectedStatus: 'paid',
                status: rollbackStatus
              })
              if (!rolledBack) throw new Error('Statement payment rollback was not saved')
            }
          })
        }
        results.push({ statementId, outcome: 'updated', reason: null })
      } catch (error) {
        const code = error && typeof error === 'object' && 'code' in error ? error.code : null
        let reason: StatementStatusChangeResult['reason'] = 'execution_failed'
        if (error instanceof StatementPaidNotificationError) {
          reason = error.rollbackFailed ? 'rollback_failed' : 'notification_failed'
        } else if (code === '42501') {
          reason = 'permission_denied'
        } else if (code === 'P0002') {
          reason = 'unavailable'
        } else if (code === '22023' || (typeof code === 'string' && code.startsWith('DST'))) {
          reason = 'ineligible'
        }
        results.push({ statementId, outcome: 'rejected', reason })
      }
    }
    return results
  }

  static async approve(params: ApproveStatementParams): Promise<StatementApprovalResult> {
    const { rpcClient, organizationId, actorId, ...input } = params
    readContextSchema.parse({ organizationId, actorId })
    const { statementId } = statementActionSchema.parse(input)
    await Statements.getVisibleStatement({ rpcClient, organizationId, actorId, statementId })
    if (!(await rpcClient.isActiveOrganizationMember(actorId, organizationId))) {
      throw new Error('Statement approval access denied')
    }

    const recorded = await rpcClient.getStatementApproval(statementId, actorId)
    let inserted = false
    let notificationStatus: StatementApprovalResult['notificationStatus'] = 'not_attempted'
    if (!recorded) {
      const config = await rpcClient.getOrganizationValidationProcessConfig(organizationId)
      if (!config.statementApprovalFlow) throw new Error('Statement approval is disabled')
      if (await rpcClient.isActiveOrganizationAdmin(actorId, organizationId)) {
        const count = await Statements.getUnreviewedDiscrepanciesCount({ rpcClient, statementId })
        if (count !== 0) throw new Error('Statement has unreviewed updates on hold')
      }
      const current = await rpcClient.getStatementReadRow(statementId, organizationId)
      if (!current || current.organization_id !== organizationId) {
        throw new Error('Statement is unavailable')
      }
      if (current.status !== 'in_review' || current.pending_approval !== true) {
        throw new Error('Statement is not actionable for approval')
      }
      if (current.has_pending_payouts) throw new Error('Statement has pending payouts')
      if (!current.nextApproverIds.includes(actorId)) {
        throw new Error('Actor is not a current statement approver')
      }
      // shortcut: checks and insert are separate; add an atomic RPC if overlapping reset/approval becomes a problem.
      try {
        await rpcClient.insertStatementApproval({ statementId, actorId })
        inserted = true
      } catch (error) {
        if (!error || typeof error !== 'object' || !('code' in error) || error.code !== '23505') {
          throw error
        }
        if (!(await rpcClient.getStatementApproval(statementId, actorId))) throw error
      }
      if (inserted) {
        try {
          await rpcClient.createStatementApprovalNotification({
            organizationId,
            statementId,
            actorId
          })
          notificationStatus = 'created'
        } catch {
          // The approval is saved; do not roll it back or repeat its notification on retry.
          notificationStatus = 'failed'
        }
      }
    }
    let current: StatementMonthRow | null = null
    try {
      current = await rpcClient.getStatementReadRow(statementId, organizationId)
      if (current?.organization_id !== organizationId) current = null
    } catch {
      // A refresh failure must not hide a successfully recorded approval.
    }
    return {
      statementId,
      approvalId: null,
      outcome: inserted ? 'updated' : 'skipped',
      reason: inserted ? null : 'already_approved',
      status: current?.status ?? null,
      remainingApproverIds: current?.nextApproverIds ?? null,
      notificationStatus
    }
  }

  static async addIssue(params: AddStatementIssueParams): Promise<StatementIssueResult> {
    const { rpcClient, organizationId, actorId, ...input } = params
    readContextSchema.parse({ organizationId, actorId })
    const { statementId } = statementActionSchema.parse(input)
    await Statements.getVisibleStatement({ rpcClient, organizationId, actorId, statementId })
    const issue = await rpcClient.insertStatementIssue({ organizationId, statementId, actorId })
    let notificationStatus: StatementIssueResult['notificationStatus'] = 'created'
    try {
      await rpcClient.createStatementIssueNotification({
        organizationId,
        issueId: issue.id,
        actorId
      })
    } catch {
      // The issue is saved; report notification failure without encouraging a duplicate issue.
      notificationStatus = 'failed'
    }
    return { issueId: issue.id, statementId, status: issue.status, notificationStatus }
  }

  static async addManualPayout(params: AddStatementManualPayoutParams): Promise<Tables<'payouts'>> {
    const { rpcClient, organizationId, actorId, ...input } = params
    readContextSchema.parse({ organizationId, actorId })
    const values = manualPayoutSchema.parse(input)
    const statement = await Statements.getVisibleStatement({
      rpcClient,
      organizationId,
      actorId,
      statementId: values.statementId
    })
    const { managedTeamIds } = await Statements.getListScope({ rpcClient, organizationId, actorId })
    if (managedTeamIds !== null) {
      const memberships = await rpcClient.listProfileCurrentTeamMembershipsSetup(
        organizationId,
        statement.profile_id
      )
      if (!memberships.some(row => row.role === 'member' && managedTeamIds.includes(row.team_id))) {
        throw new Error('Manual payout access denied')
      }
    }
    if (values.categoryId != null) {
      const category = await rpcClient.getPayoutCategoryById(values.categoryId, organizationId)
      if (!category || category.organization_id !== organizationId) {
        throw new Error('Payout category is unavailable')
      }
    }
    if (!values.exchangeRateMethod) {
      const currency = new DolfinCurrency(statement.organization_id, rpcClient)
      const [organizationCurrency, teamCurrency] = await Promise.all([
        currency.getOrgCurrency(),
        currency.getTeamCurrency(statement.profile_id, values.effectiveDate, { throwOnError: true })
      ])
      if (teamCurrency && teamCurrency !== organizationCurrency) {
        throw new Error('Exchange rate settings are required for the recipient currency')
      }
    }
    return Payouts.saveManualPayout(rpcClient, {
      categoryId: values.categoryId,
      compensationType: values.compensationType,
      customRate: values.customRate,
      description: values.description,
      effectiveDate: values.effectiveDate,
      exchangeRateDate: values.exchangeRateDate,
      exchangeRateMethod: values.exchangeRateMethod,
      organizationId: statement.organization_id,
      periodEnd: values.periodEnd,
      periodStart: values.periodStart,
      profileId: statement.profile_id,
      statementId: statement.id,
      type: values.compensationType,
      value: values.amount
    })
  }

  static async getUnreviewedDiscrepanciesCount(
    params: GetUnreviewedDiscrepanciesCountParams
  ): Promise<number> {
    const { rpcClient, statementId } = params

    return rpcClient.getUnreviewedDiscrepanciesCountForStatement(statementId)
  }

  static async getPendingApprovalsCount(
    rpcClient: DolfinRpc,
    organizationId: number,
    periodStart: string,
    periodEnd: string,
    approverUserId?: string
  ) {
    return rpcClient.getPendingStatementApprovalsCount(
      organizationId,
      periodStart,
      periodEnd,
      approverUserId
    )
  }

  static async ensureStatementsForPeriodRange(
    rpcClient: DolfinRpc,
    organizationId: number,
    startDate: string,
    endDate: string
  ) {
    return rpcClient.ensureStatementsForPeriodRange(organizationId, startDate, endDate)
  }

  static async startReview(
    params: StartStatementsReviewParams
  ): Promise<StartStatementsReviewResult> {
    const { rpcClient, organizationId, statementIds } = params

    return rpcClient.startStatementsReview(organizationId, statementIds)
  }

  static async getResetEligibility(
    rpcClient: DolfinRpc,
    statementIds: number[]
  ): Promise<StatementResetEligibility[]> {
    return rpcClient.getStatementResetEligibility(statementIds)
  }

  static async resetToPendingApproval(rpcClient: DolfinRpc, statementId: number): Promise<void> {
    return rpcClient.resetStatementToPendingApproval(statementId)
  }

  static async getMonthlyReviewFlow(
    params: GetMonthlyReviewFlowParams
  ): Promise<MonthlyStatementReviewFlowSummary> {
    const { rpcClient, organizationId, startDate, endDate } = params
    const [rows, organizationConfig] = await Promise.all([
      rpcClient.getStatementReviewFlowRows(organizationId, startDate, endDate),
      rpcClient.getOrganizationValidationProcessConfig(organizationId)
    ])

    const hasStatementApprovalFlow = organizationConfig.statementApprovalFlow
    const hasPayoutValidationFlow = organizationConfig.payoutValidationFlow
    const pendingReviewStatementIds = rows
      .filter(statement => statement.status === 'open')
      .map(statement => statement.id)
    const inReviewStatements = rows
      .filter(statement => statement.status === 'in_review')
      .sort((left, right) => left.id - right.id)
    const approvedStatements =
      hasStatementApprovalFlow || hasPayoutValidationFlow
        ? rows.filter(statement => statement.status === 'approved')
        : []

    return {
      totalCount: rows.length,
      payoutValidationFlow: hasPayoutValidationFlow,
      statementApprovalFlow: hasStatementApprovalFlow,
      counts: {
        pendingApproval: hasStatementApprovalFlow
          ? pendingReviewStatementIds.length
          : !hasPayoutValidationFlow
            ? rows.filter(
                statement => statement.status !== 'paid' && statement.status !== 'in_review'
              ).length
            : rows.filter(statement => statement.status === 'open').length,
        inReview: inReviewStatements.length,
        approved: approvedStatements.length,
        paid: rows.filter(statement => statement.status === 'paid').length
      },
      pendingReviewStatementIds,
      firstInReviewStatement: inReviewStatements[0]
        ? {
            id: inReviewStatements[0].id,
            profileId: inReviewStatements[0].profile_id,
            periodStart: inReviewStatements[0].period_start
          }
        : null,
      readyToPayStatementIds: rows
        .filter(statement =>
          isStatementEligibleForPayment({
            ...statement,
            hasStatementApprovalFlow,
            hasPayoutValidationFlow
          })
        )
        .map(statement => statement.id)
    }
  }

  static async getNotificationReferences(
    params: GetNotificationReferencesParams
  ): Promise<NotificationStatementReference[]> {
    const { rpcClient, statementIds } = params

    return rpcClient.getNotificationStatementReferences(statementIds)
  }

  static async getStatementPeriodReferences(
    params: GetStatementPeriodReferencesParams
  ): Promise<StatementPeriodReference[]> {
    const { rpcClient, statementIds } = params

    return rpcClient.getStatementPeriodReferences(statementIds)
  }
}
